import React, { MouseEvent, useMemo } from 'react';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';
import { getFullName } from 'dashboard409a/components/ModelsTable/ModelsTableRow/helpers';
import InlineNoteEditor from 'dashboard409a/components/ValuationsTable/common/InlineNoteEditor';
import WorkboardModelStatusCell from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardModelStatusCell';
import WorkboardSortHeader from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardSortHeader';
import { modelsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { MODELS_COLUMN_IDS } from 'dashboard409a/pages/Workboard/common/constants';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { ModelCompanyNameCell, ModelValuationLeadCell, ModelValuationTextCell } from './ModelsCells';

const HEADER_ALIAS = '__workboard_models_header__';
const EMPTY_ALIAS = '__workboard_models_empty__';
const SHEET_NAME = 'workboard_models';
const EMPTY_MODELS_MESSAGE = 'No models found';

const INTERACTIVE_COLUMN_IDS: Set<string> = new Set([
  MODELS_COLUMN_IDS.MODEL_STATUS,
  MODELS_COLUMN_IDS.NOTES,
]);

interface UseWorkboardModelsSheetParams {
  models: ModelWithValuationData[];
  visibleColumnIds: string[];
  isNoteLoading?: boolean;
  sortCriteria?: SortCriteria[];
  onRequestSort?: (event: MouseEvent<unknown>, property: string, addToSort?: boolean) => void;
  onStatusUpdate: (modelId: number, newStatus: number) => void;
  onNoteSave: (fulfillmentId: number, noteContent: string, currentNote?: string) => Promise<void>;
}

const useWorkboardModelsSheet = ({
  models,
  visibleColumnIds,
  isNoteLoading = false,
  sortCriteria,
  onRequestSort,
  onStatusUpdate,
  onNoteSave,
}: UseWorkboardModelsSheetParams) => {
  const spreadsheet = useMemo(() => {
    const labelMap = Object.fromEntries(modelsColumns.map(c => [c.id, c.label]));
    const sortableMap = Object.fromEntries(modelsColumns.map(c => [c.id, c.isSortable !== false]));

    const columns = visibleColumnIds.map(id => ({ id, label: labelMap[id] ?? id }));

    const rowConfig = [
      { alias: HEADER_ALIAS, gridType: 'string' as const, readOnly: true },
      ...(models.length === 0
        ? [{ alias: EMPTY_ALIAS, gridType: 'string' as const, readOnly: true }]
        : models.map(m => ({ alias: String(m.id), gridType: 'string' as const, readOnly: true }))),
    ];

    const modelMap = new Map(models.map(m => [String(m.id), m]));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parser = ({ columns: cols, rowConfig: rows }: any) => {
      const cells: Record<string, any> = {};
      const alphabet = alphabetGenerator([], cols.length);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rows.forEach((row: any, rowIndex: number) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cols.forEach((column: any, colIndex: number) => {
          const key = `${alphabet[colIndex]}${rowIndex + 1}`;

          if (row.alias === HEADER_ALIAS) {
            const isSortable = Boolean(onRequestSort) && sortableMap[column.id] !== false;
            const currentSort = sortCriteria?.find(c => c.property === column.id);
            const sortDirection = currentSort?.direction ?? null;

            cells[key] = {
              readOnly: true,
              gridType: 'string',
              columnRef: column.id,
              value: column.label,
              ...(column.id === MODELS_COLUMN_IDS.COMPANY_NAME ? { style: { textAlign: 'left' } } : {}),
              ...(isSortable
                ? {
                  forceComponent: true,
                  component: React.createElement(WorkboardSortHeader, {
                    title: column.label,
                    direction: sortDirection,
                    onClick: (event: { shiftKey: boolean }) => {
                      onRequestSort?.(event as MouseEvent<unknown>, column.id, event.shiftKey);
                    },
                  }),
                }
                : {}),
            };
            return;
          }

          if (row.alias === EMPTY_ALIAS) {
            cells[key] = {
              readOnly: true,
              gridType: 'string',
              columnRef: column.id,
              className: 'workboard-empty-message-cell',
              value: colIndex === 0 ? EMPTY_MODELS_MESSAGE : '',
              ...(colIndex === 0 ? { style: { textAlign: 'left' } } : {}),
            };
            return;
          }

          const modelRow = modelMap.get(row.alias);
          if (!modelRow) {
            cells[key] = { readOnly: true, value: '-', gridType: 'string', columnRef: column.id };
            return;
          }

          const isInteractive = INTERACTIVE_COLUMN_IDS.has(column.id);
          const baseCell = {
            readOnly: true as const,
            gridType: 'string' as const,
            columnRef: column.id,
            ...(isInteractive
              ? { className: 'workboard-interactive-cell' }
              : { className: 'workboard-row-click-cell' }),
          };

          switch (column.id) {
            case MODELS_COLUMN_IDS.COMPANY_NAME:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(ModelCompanyNameCell, { modelRow }),
                style: { textAlign: 'left' },
              };
              break;

            case MODELS_COLUMN_IDS.VALUATION_STATUS:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(ModelValuationTextCell, {
                  modelRow,
                  field: 'valuation_status',
                }),
              };
              break;

            case MODELS_COLUMN_IDS.MODEL_STATUS:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(WorkboardModelStatusCell, {
                  modelRow,
                  onStatusUpdate: (...args: Parameters<typeof onStatusUpdate>) => onStatusUpdate(...args),
                }),
              };
              break;

            case MODELS_COLUMN_IDS.SUPPORTING_DUE_DATE:
              cells[key] = {
                ...baseCell,
                value: modelRow.supporting_due_date || '-',
              };
              break;

            case MODELS_COLUMN_IDS.NOTES:
              cells[key] = {
                ...baseCell,
                className: `${baseCell.className} workboard-notes-cell`,
                forceComponent: true,
                value: '',
                ignoreExpr: true,
                style: { textAlign: 'left' },
                component: React.createElement(InlineNoteEditor, {
                  key: `${modelRow.id}-${modelRow.note?.id ?? 'none'}-${modelRow.note?.updated_at ?? ''}`,
                  // Use noteContent — Cell clones children and overwrites the `value` prop.
                  noteContent: modelRow.note?.content || modelRow.notes,
                  isLoading: isNoteLoading,
                  onSave: async (savedContent: string) => {
                    const currentNote = modelRow.note?.content || modelRow.notes || '';
                    if (modelRow.id !== undefined) {
                      await onNoteSave(modelRow.id, savedContent, currentNote);
                    }
                  },
                }),
              };
              break;

            case MODELS_COLUMN_IDS.MODEL_REVIEWER: {
              const reviewer = modelRow.reviewers?.[0];
              const reviewerName = reviewer ? getFullName(reviewer) : '-';
              const extraReviewers = (modelRow.reviewers?.length ?? 0) > 1 ? `, ${modelRow.reviewers!.length - 1} more` : '';
              cells[key] = {
                ...baseCell,
                value: reviewerName + extraReviewers,
              };
              break;
            }

            case MODELS_COLUMN_IDS.MODEL_PREPARER: {
              const preparer = modelRow.preparers?.[0];
              const preparerName = preparer ? getFullName(preparer) : '-';
              const extraPreparers = (modelRow.preparers?.length ?? 0) > 1 ? `, ${modelRow.preparers!.length - 1} more` : '';
              cells[key] = {
                ...baseCell,
                value: preparerName + extraPreparers,
              };
              break;
            }

            case MODELS_COLUMN_IDS.VALUATION_LEAD:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(ModelValuationLeadCell, { modelRow }),
              };
              break;

            case MODELS_COLUMN_IDS.VALUATION_DATE:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(ModelValuationTextCell, {
                  modelRow,
                  field: 'valuation_date',
                }),
              };
              break;

            default:
              cells[key] = { ...baseCell, value: '-' };
          }
        });
      });

      return cells;
    };

    const config = {
      name: SHEET_NAME,
      columns,
      rowConfig,
      tableData: {},
      parser,
      afterCellChanged: (changes: any[]) => changes,
      reverseParser: undefined,
      isReadOnly: true,
      showTitlesColumn: false,
      showTotalColumn: false,
      showToolbar: false,
      displayRowIndicator: false,
      displayColumnIndicator: false,
      hasColTitle: true,
      alwaysDisplayLegend: false,
      ignoreCellExpressionErrors: true,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new SpreadsheetConfig(config as any);
  }, [models, visibleColumnIds, sortCriteria, isNoteLoading, onRequestSort, onStatusUpdate, onNoteSave]);

  return { spreadsheet };
};

export default useWorkboardModelsSheet;
